import { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Row,
  Col,
  Card,
  Space,
  Tag,
  Upload,
  Progress,
  Drawer,
  Descriptions,
  Select,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined,
  ReloadOutlined,
  EyeOutlined,
  UploadOutlined,
  PlayCircleOutlined,
  DesktopOutlined,
} from '@ant-design/icons';
import { videoAPI, screenAPI } from '../services/api';
import { themeColors } from '../config/theme';

const MEDIA_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4000/api').replace(/\/api\/?$/, '');

export default function Videos() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({ page: 1, limit: 10 });
  const [filter, setFilter] = useState('all');
  const [form] = Form.useForm();
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [detailDrawer, setDetailDrawer] = useState({ open: false, record: null });
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectingId, setRejectingId] = useState(null);
  const [previewModal, setPreviewModal] = useState({ open: false, record: null });
  const [testModal, setTestModal] = useState({ open: false, record: null });
  const [screens, setScreens] = useState([]);
  const [selectedScreenId, setSelectedScreenId] = useState(null);
  const [activeTest, setActiveTest] = useState(null);
  const [testLoading, setTestLoading] = useState(false);

  useEffect(() => {
    fetchVideos();
  }, [pagination, filter]);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      };

      if (filter !== 'all') {
        params.filters = JSON.stringify({ status: filter });
      }

      const response = await videoAPI.list(params);
      setVideos(response.data.data.data);
      setTotal(response.data.data.pagination.total);
    } catch (error) {
      message.error('Failed to load videos');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file) => {
    const formData = new FormData();
    formData.append('video', file);
    formData.append('title', file.name.replace(/\.[^/.]+$/, ''));

    try {
      setUploading(true);
      setUploadProgress(0);

      const response = await videoAPI.upload(formData);

      message.success('Video uploaded successfully');
      setUploadModalOpen(false);
      setUploadProgress(0);
      fetchVideos();
    } catch (error) {
      message.error(error.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleApprove = async (id) => {
    try {
      await videoAPI.approve(id);
      message.success('Video approved');
      fetchVideos();
    } catch (error) {
      message.error('Failed to approve video');
    }
  };

  const handleReject = async (id, reason) => {
    try {
      await videoAPI.reject(id, reason);
      message.success('Video rejected');
      setRejectModalOpen(false);
      form.resetFields();
      fetchVideos();
    } catch (error) {
      message.error('Failed to reject video');
    }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Delete Video',
      content: 'Are you sure you want to delete this video?',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await videoAPI.delete(id);
          message.success('Video deleted');
          fetchVideos();
        } catch (error) {
          message.error('Failed to delete video');
        }
      },
    });
  };

  const openTestModal = async (record) => {
    setTestModal({ open: true, record });
    setSelectedScreenId(null);
    setActiveTest(null);
    try {
      const response = await screenAPI.list({ limit: 100 });
      setScreens(response.data.data.data.filter((s) => s.status === 'online'));
    } catch (error) {
      message.error('Failed to load screens');
    }
  };

  const handleTestPlay = async () => {
    if (!selectedScreenId) {
      message.warning('Please select a screen');
      return;
    }
    try {
      setTestLoading(true);
      await screenAPI.testPlay(selectedScreenId, testModal.record.id);
      message.success('Test playback started on screen');
      setActiveTest(selectedScreenId);
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to start test playback');
    } finally {
      setTestLoading(false);
    }
  };

  const handleTestStop = async () => {
    if (!activeTest) return;
    try {
      setTestLoading(true);
      await screenAPI.testStop(activeTest);
      message.success('Test playback stopped, resumed regular playlist');
      setActiveTest(null);
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to stop test playback');
    } finally {
      setTestLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'orange',
      approved: 'green',
      rejected: 'red',
    };
    return colors[status] || 'default';
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text) => (
        <div style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {text}
        </div>
      ),
    },
    {
      title: 'Filename',
      dataIndex: 'filename',
      key: 'filename',
      width: 150,
    },
    {
      title: 'Size',
      dataIndex: 'filesize',
      key: 'filesize',
      render: (bytes) => {
        if (!bytes) return 'N/A';
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
      },
      width: 100,
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
      render: (sec) => (sec ? `${Math.floor(sec / 60)}:${(sec % 60).toString().padStart(2, '0')}` : 'N/A'),
      width: 80,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <Tag color={getStatusColor(status)}>{status.toUpperCase()}</Tag>,
      width: 100,
    },
    {
      title: 'Uploaded By',
      dataIndex: ['user', 'email'],
      key: 'uploaded_by',
      render: (email) => email || 'N/A',
      width: 150,
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 240,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<PlayCircleOutlined />}
            onClick={() => setPreviewModal({ open: true, record })}
          />
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => setDetailDrawer({ open: true, record })}
          />
          {record.status === 'approved' && (
            <Button
              size="small"
              icon={<DesktopOutlined />}
              onClick={() => openTestModal(record)}
            />
          )}
          {record.status === 'pending' && (
            <>
              <Button
                type="primary"
                size="small"
                icon={<CheckOutlined />}
                style={{ backgroundColor: themeColors.success }}
                onClick={() => handleApprove(record.id)}
              />
              <Button
                danger
                size="small"
                icon={<CloseOutlined />}
                onClick={() => {
                  setRejectingId(record.id);
                  setRejectModalOpen(true);
                }}
              />
            </>
          )}
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
        <Col>
          <Button
            type="primary"
            icon={<UploadOutlined />}
            onClick={() => setUploadModalOpen(true)}
          >
            Upload Video
          </Button>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={fetchVideos} loading={loading} />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ backgroundColor: themeColors.bgElevated, borderColor: themeColors.border }}>
            <div style={{ color: themeColors.textSecondary, fontSize: '12px' }}>Pending Approval</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: themeColors.warning }}>
              {videos.filter((v) => v.status === 'pending').length}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ backgroundColor: themeColors.bgElevated, borderColor: themeColors.border }}>
            <div style={{ color: themeColors.textSecondary, fontSize: '12px' }}>Approved</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: themeColors.success }}>
              {videos.filter((v) => v.status === 'approved').length}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ backgroundColor: themeColors.bgElevated, borderColor: themeColors.border }}>
            <div style={{ color: themeColors.textSecondary, fontSize: '12px' }}>Rejected</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: themeColors.error }}>
              {videos.filter((v) => v.status === 'rejected').length}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ backgroundColor: themeColors.bgElevated, borderColor: themeColors.border }}>
            <div style={{ color: themeColors.textSecondary, fontSize: '12px' }}>Total Videos</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: themeColors.primary }}>
              {total}
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
        <Col xs={24} sm={12} md={6}>
          <Button
            block
            type={filter === 'all' ? 'primary' : 'default'}
            onClick={() => setFilter('all')}
          >
            All Videos
          </Button>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Button
            block
            type={filter === 'pending' ? 'primary' : 'default'}
            onClick={() => setFilter('pending')}
          >
            Pending
          </Button>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Button
            block
            type={filter === 'approved' ? 'primary' : 'default'}
            onClick={() => setFilter('approved')}
          >
            Approved
          </Button>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Button
            block
            type={filter === 'rejected' ? 'primary' : 'default'}
            onClick={() => setFilter('rejected')}
          >
            Rejected
          </Button>
        </Col>
      </Row>

      <Card style={{ backgroundColor: themeColors.bgElevated, borderColor: themeColors.border }}>
        <Table
          columns={columns}
          dataSource={videos}
          loading={loading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total,
            onChange: (page, pageSize) => {
              setPagination({ page, limit: pageSize });
            },
          }}
          rowKey="id"
          style={{ color: themeColors.text }}
        />
      </Card>

      {/* Upload Modal */}
      <Modal
        title="Upload Video"
        open={uploadModalOpen}
        onCancel={() => {
          setUploadModalOpen(false);
          setUploadProgress(0);
        }}
        footer={null}
        style={{ backgroundColor: themeColors.bgElevated }}
      >
        <div style={{ marginTop: '20px' }}>
          <Upload.Dragger
            beforeUpload={(file) => {
              handleUpload(file);
              return false;
            }}
            disabled={uploading}
            accept=".mp4,.mov,.avi,.mkv"
          >
            <UploadOutlined style={{ fontSize: '48px', color: themeColors.primary }} />
            <p style={{ marginTop: '16px', color: themeColors.text }}>
              Click or drag video file here
            </p>
            <p style={{ color: themeColors.textSecondary, fontSize: '12px' }}>
              Supported: MP4, MOV, AVI, MKV (Max 1GB)
            </p>
          </Upload.Dragger>

          {uploading && (
            <div style={{ marginTop: '20px' }}>
              <Progress percent={uploadProgress} status={uploading ? 'active' : 'success'} />
            </div>
          )}
        </div>
      </Modal>

      {/* Preview Modal */}
      <Modal
        title={`Preview: ${previewModal.record?.title || ''}`}
        open={previewModal.open}
        onCancel={() => setPreviewModal({ open: false, record: null })}
        footer={null}
        width={720}
        destroyOnClose
        style={{ backgroundColor: themeColors.bgElevated }}
      >
        {previewModal.record && (
          <video
            key={previewModal.record.id}
            controls
            autoPlay
            style={{ width: '100%', maxHeight: '500px', backgroundColor: '#000' }}
            src={`${MEDIA_BASE_URL}/uploads/videos/${previewModal.record.filename}`}
          >
            Your browser does not support the video tag.
          </video>
        )}
      </Modal>

      {/* Test on Screen Modal */}
      <Modal
        title={`Test Play: ${testModal.record?.title || ''}`}
        open={testModal.open}
        onCancel={() => setTestModal({ open: false, record: null })}
        footer={null}
        style={{ backgroundColor: themeColors.bgElevated }}
      >
        <div style={{ marginTop: '12px' }}>
          <p style={{ color: themeColors.textSecondary, marginBottom: '12px' }}>
            Select an online screen to play this video directly for testing. This will pause
            the regular playlist on that screen until you stop the test.
          </p>
          <Select
            style={{ width: '100%', marginBottom: '16px' }}
            placeholder="Select a screen"
            value={selectedScreenId}
            onChange={setSelectedScreenId}
            disabled={!!activeTest}
            options={screens.map((s) => ({
              value: s.id,
              label: `${s.screen_name} (${s.screen_code})`,
            }))}
            notFoundContent="No online screens"
          />
          <Space>
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              loading={testLoading}
              disabled={!!activeTest}
              onClick={handleTestPlay}
            >
              Play on Screen
            </Button>
            <Button
              danger
              loading={testLoading}
              disabled={!activeTest}
              onClick={handleTestStop}
            >
              Stop Test
            </Button>
          </Space>
        </div>
      </Modal>

      {/* Reject Modal */}
      <Modal
        title="Reject Video"
        open={rejectModalOpen}
        onOk={() => {
          form.validateFields().then((values) => {
            handleReject(rejectingId, values.reason);
          });
        }}
        onCancel={() => {
          setRejectModalOpen(false);
          form.resetFields();
          setRejectingId(null);
        }}
        style={{ backgroundColor: themeColors.bgElevated }}
      >
        <Form form={form} layout="vertical" style={{ marginTop: '20px' }}>
          <Form.Item
            name="reason"
            label="Reason for Rejection"
            rules={[{ required: true, message: 'Please provide a reason' }]}
          >
            <Input.TextArea rows={4} placeholder="Explain why this video is being rejected..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Detail Drawer */}
      <Drawer
        title="Video Details"
        placement="right"
        onClose={() => setDetailDrawer({ open: false, record: null })}
        open={detailDrawer.open}
        width={400}
      >
        {detailDrawer.record && (
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="ID">{detailDrawer.record.id}</Descriptions.Item>
            <Descriptions.Item label="Title">{detailDrawer.record.title}</Descriptions.Item>
            <Descriptions.Item label="Filename">{detailDrawer.record.filename}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={getStatusColor(detailDrawer.record.status)}>
                {detailDrawer.record.status.toUpperCase()}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Size">
              {(detailDrawer.record.filesize / (1024 * 1024)).toFixed(2)} MB
            </Descriptions.Item>
            <Descriptions.Item label="Duration">
              {Math.floor(detailDrawer.record.duration / 60)}:
              {(detailDrawer.record.duration % 60).toString().padStart(2, '0')} seconds
            </Descriptions.Item>
            <Descriptions.Item label="Checksum">
              <code style={{ fontSize: '11px' }}>{detailDrawer.record.checksum?.substring(0, 16)}...</code>
            </Descriptions.Item>
            <Descriptions.Item label="Uploaded">
              {new Date(detailDrawer.record.created_at).toLocaleString()}
            </Descriptions.Item>
            {detailDrawer.record.rejection_reason && (
              <Descriptions.Item label="Rejection Reason">
                {detailDrawer.record.rejection_reason}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Drawer>
    </div>
  );
}
